/**
 * RecordRow Component
 *
 * Individual record row for action cards.
 * Matches the RecordsTable styling with conduct score, date, customer, and summary.
 *
 * Atomic level: Molecule
 * Used by: ActionCardsSection (Eligible Complaints card)
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { ConductScoreCircle } from '../../../ui/conduct-score-circle';
import type { RecordRowProps } from '../types/today.types';

/**
 * Format time (e.g., "3:45 PM")
 */
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Individual record row for action cards, matching RecordsTable styling.
 */
export function RecordRow({
  record,
  onClick,
  showConductScore = true,
}: RecordRowProps): React.ReactElement {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none"
      onClick={() => onClick(record.id, record.collectionId)}
    >
      {/* Conduct Score */}
      {showConductScore && record.conductScore !== null && (
        <ConductScoreCircle score={record.conductScore} size="sm" className="shrink-0" />
      )}

      {/* Time */}
      <span className="w-20 flex-shrink-0 text-[12px] text-muted-foreground">
        {formatTime(record.receivedAt)}
      </span>

      {/* Customer Name */}
      <span className="w-32 flex-shrink-0 truncate text-[13px] font-medium text-foreground">
        {record.customerName || '—'}
      </span>

      {/* Summary */}
      <p className="flex-1 truncate text-[13px] text-muted-foreground">{record.summary}</p>

      {/* Chevron */}
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
    </button>
  );
}
